//@ts-check
import { useState, useCallback, useMemo } from 'react';
import {
  getUserStatus,
  StatusStoreState,
} from '@/context_providers/group_user_context/OrgGroupUserStatusStore';
import { Link, Alert, Modal, Paper, Typography } from '@mui/material';
import { useBannerHeight } from '@/context_providers/banner_context/BannerHeightContext';
import { useLocationSharingActions } from '@/stores/map/locationSharingStore';
import {
  useDashboardUserAuthId,
  useDashboardActions,
  useIsDashboardOpen,
  useDashboardDeviceOverride,
} from '@/stores/map/dashboardStore';
import UserDashboardHeader from './user_dashboard_components/UserDashboardHeader';
import DeviceOnlyDashboardHeader from './user_dashboard_components/DeviceOnlyDashboardHeader';
import DashboardTab from './user_dashboard_components/DashboardTab';
import DeveloperTab from './user_dashboard_components/DeveloperTab';
import { useUserUpdaterProvider } from '@/context_providers/user_context/UserUpdaterProvider';
import { isAdmin } from '@/components/routing/UserRoutes/AcceptedRoles';
import Group from '@/models/Organization/Group';
import {
  LocationPreference,
  OrganizationOnAzure,
} from '../../../../../generated-client/models';
import type { PvdConfigurationOnAzure } from '@/../generated-client/models';
import { StringKeys } from '@/resources/strings/StringKeys';
import {
  formatPhoneForDisplay,
  phoneTelHref,
} from '@/utils/device_helper/formatPhoneForDisplay';
import { t } from 'i18next';

export type UserRole = 'Admin' | 'Dispatch' | 'Mobile User' | string;

export interface UserInfo {
  groupName: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  role: UserRole;
  userAuthId: string;
  orgUserAzureId: string;
  pvdConfigId: string | null;
  emergencyConfigId: string | null;
  locationPreference: LocationPreference;
  languageCode: string | null;
  deviceLocationPermission: string | null;
  shouldForceApplyConfig: boolean;
}

type UserDashboardProps = {
  organization: OrganizationOnAzure;
  groups: Group[];
  pvdConfigurations: PvdConfigurationOnAzure[] | undefined;
  allGroupUserStatuses: StatusStoreState;
};

/**
 * A dashboard to display information about a given user
 * @param props
 * @returns
 */
const UserDashboard = ({
  organization,
  groups,
  pvdConfigurations,
  allGroupUserStatuses,
}: UserDashboardProps) => {
  const locationSharingActions = useLocationSharingActions();
  const dashboardUserAuthId = useDashboardUserAuthId();
  const isDashboardOpen = useIsDashboardOpen();
  const dashboardActions = useDashboardActions();
  const dashboardDeviceOverride = useDashboardDeviceOverride();

  // The viewer (not the dashboard's target user) determines mutation rights —
  // Dispatch (and any non-Admin role) gets a read-only dashboard (Task 20/WD4).
  const { orgUser } = useUserUpdaterProvider();
  const isReadOnly = !isAdmin(orgUser?.role);

  const { emergencyBannerHeight, developmentBannerHeight } = useBannerHeight();

  const [tabValue, setTabValue] = useState(0);

  const navbarHeight = 65;
  const totalBannerHeight = emergencyBannerHeight + developmentBannerHeight;
  const totalTopOffset = navbarHeight + totalBannerHeight;

  // Get active auth ID either from the location marker or from the dashboard store
  const activeUserAuthId = useMemo(() => {
    return dashboardUserAuthId;
  }, [dashboardUserAuthId]);

  const getUserInfo = useCallback((): UserInfo | null => {
    if (!groups || !Array.isArray(groups) || !activeUserAuthId) return null;

    const userAuthId = activeUserAuthId;

    // Find the user in any group if no specific group is selected
    for (const group of groups) {
      const userDetails = group.group_value.find(
        (user) => user.auth_id === userAuthId,
      );

      if (userDetails) {
        return {
          groupName: group.group_name,
          firstName: userDetails.first_name,
          lastName: userDetails.last_name,
          phoneNumber: userDetails.phone_number,
          role: userDetails.role,
          userAuthId: userAuthId,
          pvdConfigId: userDetails.pvd_config_id ?? null,
          emergencyConfigId: userDetails.emergency_config_id ?? null,
          orgUserAzureId: userDetails.org_user_azure_id || userDetails.id,
          locationPreference: userDetails.location_preference,
          languageCode: userDetails.language_code,
          shouldForceApplyConfig: userDetails.should_force_apply_config,
          deviceLocationPermission: userDetails.device_location_permission,
        };
      }
    }

    return null;
  }, [groups, activeUserAuthId]);

  const userInfo = useMemo(() => getUserInfo(), [getUserInfo]);

  const isDeviceOnly = !userInfo && !!dashboardDeviceOverride;

  const userStatus = useMemo(() => {
    if (!userInfo?.userAuthId) return null;
    void allGroupUserStatuses; // Ensure we re-run this when statuses update
    const statusData = getUserStatus(userInfo.userAuthId);
    return statusData?.userStatus || null;
  }, [userInfo, allGroupUserStatuses]);

  const handleClose = useCallback(() => {
    locationSharingActions.setSelectedLocationMarker(null);

    dashboardActions.closeDashboard();
  }, [locationSharingActions, dashboardActions]);

  if (!isDashboardOpen) {
    return null;
  }
  if (!userInfo && !isDeviceOnly) {
    return null;
  }

  return (
    // RESOLVED (current): this `Paper` is now wrapped in MUI's own `Modal` primitive instead
    // of being rendered bare - see the long comment on the `zIndex` line below for the full
    // history (a hardcoded `1300`, then a manual bump to `theme.zIndex.modal + 1`, both now
    // superseded by this). `onClose={handleClose}` wires Escape-key and backdrop-click the
    // same way `Dialog`/`FirmwareDetailsDialog.tsx` already do, for the exact same reason:
    // `Modal` is what actually gives this overlay real modal semantics (its own `FocusTrap`,
    // its own `aria-hidden`-exemption via `ModalManager`, and - since `Modal`/`Dialog` share
    // one module-level `ModalManager` singleton (`@mui/material/Modal/useModal.js`) - correct
    // *stacking* against another already-open `Dialog`, the same way MUI already handles two
    // legitimately-nested `Dialog`s. See `FirmwareDetailsDialog.tsx`'s `handleDeviceClick`
    // comment for why this was needed and how it was verified (real-browser tests, not just
    // this reasoning).
    //
    // Backdrop: left at its default (shown), not `hideBackdrop`. Deliberate, not an
    // oversight - and evaluated against all 5 of this component's mount points, not just the
    // new stacked-with-a-Dialog case this was built for:
    //   - On the 4 pre-existing mount points (`Map.jsx`, `UserTable.tsx`,
    //     `DeviceManagement.tsx`, `GroupManagement.tsx`), where nothing else is open behind
    //     this card, a dimmed backdrop behind a detail overlay is the ordinary, expected look
    //     for this kind of surface - it does not read as new/wrong just because nothing else
    //     happens to be open underneath.
    //   - On `Insights.tsx` (the new case), with `FirmwareDetailsDialog` still open
    //     underneath, that same backdrop now also dims the *dialog*, not just the page -
    //     which reads as "the background dialog recedes, the device card is what currently
    //     has focus", the same visual language real nested/stacked modals already use
    //     elsewhere (e.g. a confirm dialog opened from within a settings dialog dimming the
    //     settings dialog behind it). Both contexts are "dim whatever is behind the topmost
    //     surface" - coherent in either case, not two different rules.
    //   - Side effect worth naming explicitly (not hidden): a backdrop also means clicking
    //     outside this card now closes it (`onClose` fires with reason `'backdropClick'`,
    //     handled identically to Escape below). This is a new affordance on the 4 pre-existing
    //     pages (previously nothing happened on an outside click), but it matches this
    //     codebase's own `Dialog` convention already in effect one layer up
    //     (`FirmwareDetailsDialog.tsx` passes `onClose` unconditionally to `Dialog`, so
    //     clicking outside *it* already closes it today) - so this isn't a new interaction
    //     pattern for the app, just this component adopting the one every `Dialog` here
    //     already has.
    <Modal open={isDashboardOpen} onClose={handleClose}>
      <Paper
        elevation={8}
        sx={{
          fontSize: 16,
          position: 'fixed',
          left: {
            xs: 0,
            sm: '50%',
          },
          top: {
            xs: 0,
            sm: `calc(40% + ${totalTopOffset}px)`,
          },
          transform: {
            xs: 'none',
            sm: 'translate(-50%, -50%)',
            md: 'translate(-50%, -50%)',
            lg: 'translate(-50%, -50%)',
          },
          transformOrigin: 'center center',
          width: {
            xs: '100vw',
            md: 'calc(75vw - 240px)',
            lg: 'calc(80vw - 240px)',
          },
          maxWidth: '950px',
          // minHeight: '580px',
          maxHeight: {
            xs: '100vh',
            sm: '80vh',
          },
          overflowY: 'auto',
          borderRadius: {
            sm: '8px',
            md: 3,
          },
          // History of this line, kept per NFR-010 rather than deleted, since the two earlier
          // fixes each solved a real (but, it turned out, incomplete) part of the same problem:
          //   1. Originally a hardcoded `1300` (== MUI's default `theme.zIndex.modal`, this
          //      repo's theme does not override it).
          //   2. UPDATED (previous round): bumped to `theme.zIndex.modal + 1` so this `Paper` -
          //      at the time a plain `position: fixed` element, never portaled, rendered inline
          //      wherever `<UserDashboard>` is mounted - would reliably paint above a
          //      simultaneously-open `Dialog` instead of tying with one at the same literal
          //      value (a tie that resolves by DOM order, and previously favored the `Dialog` -
          //      see FirmwareDetailsDialog.tsx's `handleDeviceClick` comment). Verified via a
          //      real-browser computed-style test that this numeric comparison alone held
          //      (`UserDashboard.deviceOnly.test.tsx`) - true as far as it went, but that same
          //      round's investigation also found painting on top was NOT sufficient: MUI's
          //      `Dialog` still marked this `Paper` `aria-hidden` (via `ModalManager`) and kept
          //      the sole active `FocusTrap`, regardless of z-index.
          //   3. RESOLVED (current): this `Paper` is now wrapped in a real `Modal` (see just
          //      above this `sx` block), which is what actually fixes the aria-hidden/focus-trap
          //      half - see that comment for how. Once wrapped, this line's own manual bump is
          //      superseded and removed: `Modal`'s own root (`.MuiModal-root`, the actual
          //      `position: fixed` element now covering the viewport, with this `Paper` nested
          //      inside it) carries MUI's default `zIndex: theme.zIndex.modal` unconditionally -
          //      the SAME literal value as `Dialog`'s own modal root, once again a numeric tie.
          //      That tie is no longer a bug, though: since `Modal` and `Dialog` share one
          //      module-level `ModalManager` singleton, and this dashboard is always opened by a
          //      user action *after* `FirmwareDetailsDialog` is already open, its `Modal` portal
          //      is always appended later in `document.body`'s children - the same DOM-order
          //      tiebreak that previously favored `Dialog` (as the later one) now favors
          //      whichever modal opened most recently, i.e. this one, without needing a manual
          //      z-index override at all. Verified with the same real-browser approach as
          //      before, not just this reasoning (see `UserDashboard.deviceOnly.test.tsx`).
        }}
      >
        {/* Header Component */}
        {isDeviceOnly ? (
          <DeviceOnlyDashboardHeader
            onClose={handleClose}
            lastUserDisplayName={
              dashboardDeviceOverride?.lastUserDisplayName ?? null
            }
            lastUserPhoneNumber={
              dashboardDeviceOverride?.lastUserPhoneNumber ?? null
            }
          />
        ) : (
          <UserDashboardHeader
            userInfo={userInfo}
            userStatus={userStatus}
            onClose={handleClose}
            tabValue={tabValue}
            setTabValue={setTabValue}
          />
        )}
        {isDeviceOnly &&
          !!dashboardUserAuthId &&
          !!dashboardDeviceOverride?.lastUserDisplayName &&
          !!dashboardDeviceOverride?.lastUserPhoneNumber && (
            <Alert severity="warning" sx={{ width: '100%', mt: 1, mb: 1 }}>
              <Typography component="span">
                {t(StringKeys.LAST_WORKER_OUTSIDE_ORG_BANNER_PREFIX)}
              </Typography>{' '}
              <Typography component="span">
                {dashboardDeviceOverride?.lastUserDisplayName ?? '--'}
              </Typography>{' '}
              -{' '}
              {dashboardDeviceOverride?.lastUserPhoneNumber ? (
                <Link
                  href={
                    phoneTelHref(
                      dashboardDeviceOverride?.lastUserPhoneNumber ?? '',
                    ) ?? ''
                  }
                >
                  {formatPhoneForDisplay(
                    dashboardDeviceOverride?.lastUserPhoneNumber ?? '',
                  )}
                </Link>
              ) : (
                formatPhoneForDisplay(
                  dashboardDeviceOverride?.lastUserPhoneNumber ?? '',
                )
              )}
            </Alert>
          )}
        {/* Tabs Content */}
        <DashboardTab
          userStatus={userStatus}
          value={tabValue}
          organization={organization}
          groups={groups}
          pvdConfigurations={pvdConfigurations}
          index={0}
          isReadOnly={isReadOnly}
        />
        {/* {tabValue == 2 && <div></div>} */}
        {!isDeviceOnly && userInfo && (
          <DeveloperTab value={tabValue} index={1} userInfo={userInfo} />
        )}
      </Paper>
    </Modal>
  );
};

export default UserDashboard;
